import { Fragment, ReactNode } from 'react'
import {
  DraftQuestionnaireEntry,
  EntryType,
  QuestionTypeCode
} from '../../../data/model/questionnaire'
import { BasicQuestionContainer } from '../BasicQuestion'
import { ConjointQuestion } from '../ConjointQuestion'
import { ForkContainer } from '../Fork'
import { FreeTextQuestionContainer } from '../FreeTextQuestion'
import { KpiProvider } from '../Kpi/KpiProvider'
import { MarketVisibilityDialogProvider } from '../MarketVisibility/MarketVisibilityDialogProvider'
import { MatrixQuestionContainer } from '../MatrixQuestion'
import MaxDiffQuestionContainer from '../MaxDiffQuestion/MaxDiffQuestion.container'
import { SliderQuestionContainer } from '../SliderQuestion'
import { TextCardContainer } from '../TextCard'
import TextHighlighterQuestionCard from '../TextHighlighter/TextHighlighterQuestionCard'
import { useRefusedEntryResets } from './refusedEntryResets'

const renderEntryCard = (
  entry: DraftQuestionnaireEntry,
  isNewlyCreated: boolean
) => {
  const { entryItem, entryType } = entry

  switch (entryType) {
    case EntryType.QuestionEntryType:
      switch (entryItem.questionTypeCode) {
        case QuestionTypeCode.Basic:
        case QuestionTypeCode.Ranked:
          return (
            <BasicQuestionContainer
              key={entryItem.questionLk}
              entry={entry}
              shouldTitleInputFocus={isNewlyCreated}
            />
          )
        case QuestionTypeCode.FreeText:
          return (
            <FreeTextQuestionContainer
              key={entryItem.questionLk}
              entry={entry}
              shouldTitleInputFocus={isNewlyCreated}
            />
          )
        case QuestionTypeCode.Scale:
          return (
            <SliderQuestionContainer
              key={entryItem.questionLk}
              entry={entry}
              shouldTitleInputFocus={isNewlyCreated}
            />
          )
        case QuestionTypeCode.MaxDiff:
          return (
            <MaxDiffQuestionContainer
              key={entryItem.questionLk}
              entry={entry}
              shouldTitleInputFocus={isNewlyCreated}
            />
          )
      }
      break
    case EntryType.TextCardEntryType:
      return (
        <TextCardContainer
          key={entry.id}
          entry={entry}
          shouldTitleInputFocus={isNewlyCreated}
        />
      )
    case EntryType.MatrixEntryType:
      return (
        <MatrixQuestionContainer
          key={entryItem.matrixTitleLk}
          entry={entry}
          shouldTitleInputFocus={isNewlyCreated}
        />
      )
    case EntryType.ForkEntryType:
      return <ForkContainer entry={entry} />
    case EntryType.TextHighlighterEntryType:
      return (
        <TextHighlighterQuestionCard
          entry={entry}
          isNewlyCreated={isNewlyCreated}
        />
      )
    case EntryType.ConjointEntryType:
      return (
        <ConjointQuestion
          key={entry.id}
          entry={entry}
          isNewlyCreated={isNewlyCreated}
        />
      )
  }

  return null
}

const ResetOnRefusal = ({
  entryId,
  children
}: {
  entryId: string
  children: ReactNode
}) => {
  const resets = useRefusedEntryResets(entryId)
  return <Fragment key={resets}>{children}</Fragment>
}

export const getEntryCard = (
  entry: DraftQuestionnaireEntry,
  newlyAddedEntryNumber?: number
) => {
  const isNewlyCreated = newlyAddedEntryNumber === entry.number
  const card = renderEntryCard(entry, isNewlyCreated)

  // Forks are structural and have no market visibility, so they skip the provider;
  // every real question/matrix/text card is wrapped so its header icon, kebab and
  // bottom banner can all open the one market-visibility dialog.
  if (!card || entry.entryType === EntryType.ForkEntryType) {
    return card
  }

  return (
    <MarketVisibilityDialogProvider entry={entry}>
      <KpiProvider key={entry.id} entry={entry}>
        <ResetOnRefusal entryId={entry.id}>{card}</ResetOnRefusal>
      </KpiProvider>
    </MarketVisibilityDialogProvider>
  )
}
